import { useState, type ReactNode } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { CurrentMax, Draft, DraftLineOptions, UnitsPreference } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { DayMenu, type DayMenuMode } from "./DayMenu";
import { DayStructure } from "./DayStructure";
import { ExerciseRoundGrid, type GridEdit } from "./ExerciseRoundGrid";
import type { useRevealFocusedField } from "./useRevealFocusedField";
import type { V3Navigation } from "./useV3Navigation";
import { SCROLL_END, type V3Styles } from "./v3Styles";

/** The mock's zones (`.wg`): the structure and the exercise, in pt. From
 *  1100 the exercise gives the grid room for six rounds at 1280. */
const LEFT_W = 240;
const RIGHT_W = { medium: 330, large: 300 } as const;

// The editor's body on a wide screen (#336 slice 6, brief §7.3). From 1100
// three zones as the mock draws them: the structure, the day's grid, the open
// exercise. The grid carries the heatmap's bands (§15 row 28). Between 768
// and 1100 the structure goes and the day chips with «+ Day» take its place,
// the day menu moves into the grid's head. The exercise pane is the same slot in both, so a resize keeps
// it (useMasterDetailSlots' reason). `top` and `panels` are what the phone
// scroller has above and below the day. Without `edit` (#358) it is read
// only; `inline`, it grows with its content in a page that scrolls, with no
// room kept at the end for a keyboard.
export function WideEditorBody({
  theme,
  styles,
  tier,
  draft,
  nav,
  edit,
  lineOptions,
  cellUnit,
  resolveName,
  maxMeta,
  lockedThrough,
  inline = false,
  top,
  navigator,
  panels,
  detail,
}: {
  theme: UiTheme;
  styles: V3Styles;
  tier: "medium" | "large";
  draft: Draft;
  nav: V3Navigation;
  /** The grid's writes, and the keyboard reveal on the pane with the fields. */
  edit?: GridEdit & {
    detailScroll: Pick<ReturnType<typeof useRevealFocusedField>, "scrollRef" | "onScroll" | "keyboardHeight">;
  };
  lineOptions: DraftLineOptions;
  cellUnit: UnitsPreference;
  resolveName: (id: string, snapshot?: string) => string;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** Rounds 0..lockedThrough were executed. */
  lockedThrough?: number;
  inline?: boolean;
  top: ReactNode;
  /** The day chips, where there is no structure. */
  navigator: ReactNode;
  panels: ReactNode;
  /** The open exercise, or null when the day has none. */
  detail: ReactNode;
}) {
  const { t } = useTranslation();
  const p = theme.palette;
  const [menu, setMenu] = useState<DayMenuMode>(null);
  const days = edit?.days;
  const detailScroll = edit?.detailScroll;
  const openDayMenu = (sIdx: number) => {
    if (sIdx !== nav.dayIdx) nav.selectDay(sIdx);
    setMenu("menu");
  };
  return (
    <View style={[local.columns, inline && local.inline]}>
      {tier === "large" ? (
        <ScrollView style={[local.left, { borderRightColor: p.border, backgroundColor: p.surface }]}>
          <DayStructure
            theme={theme}
            styles={styles}
            draft={draft}
            nav={nav}
            days={days}
            resolveName={resolveName}
            onDayMenu={days && openDayMenu}
          />
        </ScrollView>
      ) : null}
      <ScrollView style={local.center} contentContainerStyle={inline ? null : local.scrollEnd} keyboardShouldPersistTaps="handled">
        {top}
        {navigator}
        {days ? (
          <View style={styles.gutter}>
            <DayMenu theme={theme} styles={styles} draft={draft} sIdx={nav.dayIdx} mode={menu} setMode={setMenu} days={days} />
          </View>
        ) : null}
        <ExerciseRoundGrid
          theme={theme}
          styles={styles}
          draft={draft}
          nav={nav}
          edit={edit}
          lineOptions={lineOptions}
          cellUnit={cellUnit}
          resolveName={resolveName}
          maxMeta={maxMeta}
          lockedThrough={lockedThrough}
          onDayMenu={days && tier === "medium" ? () => openDayMenu(nav.dayIdx) : undefined}
        />
        {panels}
      </ScrollView>
      <ScrollView
        ref={detailScroll?.scrollRef}
        style={[local.right, { width: RIGHT_W[tier], borderLeftColor: p.border }]}
        contentContainerStyle={inline ? null : { paddingBottom: SCROLL_END + (detailScroll?.keyboardHeight ?? 0) }}
        onScroll={detailScroll?.onScroll}
        scrollEventThrottle={32}
        keyboardShouldPersistTaps="handled"
      >
        {detail ?? (
          <Text style={[styles.section, styles.muted]}>
            {t("editorV3.grid.pickExercise", { defaultValue: "Pick an exercise in the grid to see it here." })}
          </Text>
        )}
      </ScrollView>
    </View>
  );
}

const local = StyleSheet.create({
  columns: { flex: 1, flexDirection: "row" },
  inline: { flexGrow: 0, flexShrink: 0, flexBasis: "auto" },
  left: { width: LEFT_W, flexGrow: 0, borderRightWidth: 1 },
  center: { flex: 1 },
  right: { flexGrow: 0, borderLeftWidth: 1 },
  scrollEnd: { paddingBottom: SCROLL_END },
});
